// Lossless conversion between our cornerstone-tools measurement state and the IMEXHS / ris annotation
// wire format (docs/imexhs-backend-data.md "Annotation wire format", ris viewer-api-contract §4).
//
// `points` are image-pixel coordinates (x = column, y = row) of the tool's handles, flattened in the fixed
// per-type order; `results` are `[value, unit, label]`. Results are written from the geometry on save;
// on load only the handles (and the text of text annotations) are used, and every value is recomputed
// by the tools when they render. Pure module: no DOM, no cornerstone, so it runs in plain node too.
import type { MeasurementData } from './tools/cstypes';
import {
  BIDIRECTIONAL,
  CARDIOTHORACIC_INDEX,
  COXOMETRY,
  ellipseArea,
  type HandleMap,
  measureAngle,
  measureBidirectional,
  measureCardiothoracic,
  measureCobb,
  measureCoxometry,
  measureInsallSalvati,
  measureLength,
  measureParallelDistance,
  measureTwoLineAngle,
  PARALLEL_DISTANCE,
  type Point,
  polygonArea,
  rectangleArea,
  round,
  type Spacing,
  spacingFrom,
  TWO_LINES,
} from './tools/geometry';

export const ANNOTATION_TYPES = [
  'POINT', 'LINE', 'ARROW', 'ELLIPSE', 'RECTANGLE', 'POLYGON', 'ANGLE', 'COBB-ANGLE', 'FEMORAL-ANTEVERSION',
  'BIDIRECTIONAL', 'PARALLEL-DISTANCE', 'CARDIOTHORACIC-INDEX', 'INSALL-SALVATI', 'COXOMETRY', 'SPINE-LABELING', 'TEXT',
] as const;

export type AnnotationType = (typeof ANNOTATION_TYPES)[number];
export type ResultUnit = 'mm' | 'pixels' | 'mm^2' | 'pixels^2' | 'HU' | 'deg' | 'g/ml' | '';
export type StoredResult = [number | string, ResultUnit, string];

/** One measurement in the wire format (without its image address). */
export interface StoredAnnotation {
  type: AnnotationType;
  points: number[];
  results: StoredResult[];
}

/** What the converter needs to know about the image a measurement is on. */
export interface ImageContext {
  /** DICOM pixel spacing (mm); missing or 0 means the image is uncalibrated (lengths in pixels). */
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
  /** Series modality ('CT' → HU, 'PT' → SUV). */
  modality: string;
}

/** A measurement ready to be added to cornerstone-tools' state under `toolName`. */
export interface ToolMeasurement {
  toolName: string;
  data: MeasurementData;
}

/** cornerstone tool name (as registered in src/tools.ts) → wire type. */
export const TYPE_BY_TOOL: Readonly<Record<string, AnnotationType>> = {
  Probe: 'POINT',
  Length: 'LINE',
  ArrowAnnotate: 'ARROW',
  EllipticalRoi: 'ELLIPSE',
  RectangleRoi: 'RECTANGLE',
  FreehandMouse: 'POLYGON',
  Angle: 'ANGLE',
  CobbAngle: 'COBB-ANGLE',
  FemoralAnteversion: 'FEMORAL-ANTEVERSION',
  Bidirectional: 'BIDIRECTIONAL',
  ParallelDistance: 'PARALLEL-DISTANCE',
  CardiothoracicIndex: 'CARDIOTHORACIC-INDEX',
  InsallSalvati: 'INSALL-SALVATI',
  Coxometry: 'COXOMETRY',
  TextMarker: 'SPINE-LABELING',
  TextAnnotation: 'TEXT',
};

export const TOOL_BY_TYPE: Readonly<Record<AnnotationType, string>> = Object.fromEntries(
  Object.entries(TYPE_BY_TOOL).map(([tool, type]) => [type, tool]),
) as Record<AnnotationType, string>;

/** Handle names per type, in wire order (POLYGON: every vertex, in order). */
export const HANDLE_ORDER: Readonly<Record<Exclude<AnnotationType, 'POLYGON'>, readonly string[]>> = {
  POINT: ['end'],
  LINE: ['start', 'end'],
  ARROW: ['start', 'end'],
  ELLIPSE: ['start', 'end'],
  RECTANGLE: ['start', 'end'],
  ANGLE: ['start', 'middle', 'end'],
  'COBB-ANGLE': TWO_LINES.order,
  'FEMORAL-ANTEVERSION': TWO_LINES.order,
  'INSALL-SALVATI': TWO_LINES.order,
  BIDIRECTIONAL: BIDIRECTIONAL.order,
  'PARALLEL-DISTANCE': PARALLEL_DISTANCE.order,
  'CARDIOTHORACIC-INDEX': CARDIOTHORACIC_INDEX.order,
  COXOMETRY: COXOMETRY.order,
  'SPINE-LABELING': ['end'],
  TEXT: ['end'],
};

/** Minimum `area` of a savable ELLIPSE / RECTANGLE / POLYGON (the contract rejects smaller). */
export const MIN_AREA = 1e-6;
/** Longest text the contract accepts. */
export const MAX_TEXT_LENGTH = 500;
const MIN_POLYGON_VERTICES = 3;

/** Decimals IMEXHS stores per result (undefined = full precision). */
const DECIMALS = { angle: 2, length: 2, bidirectional: 1, cardiothoracicLength: 3, index: 2, ratio: 4 } as const;
const TEXT_TYPES = new Set<AnnotationType>(['ARROW', 'SPINE-LABELING', 'TEXT']);
const ROI_TYPES = new Set<AnnotationType>(['ELLIPSE', 'RECTANGLE', 'POLYGON']);

/** Numbers in `points` a valid item of this type has (POLYGON: at least 6, even). */
export function expectedPointCount(type: AnnotationType): number {
  return type === 'POLYGON' ? MIN_POLYGON_VERTICES * 2 : HANDLE_ORDER[type].length * 2;
}

/** Why an item would be rejected by the contract's validation, or undefined when it is valid. */
export function validateStored(item: StoredAnnotation): string | undefined {
  if (!(ANNOTATION_TYPES as readonly string[]).includes(item.type)) return `unknown type ${String(item.type)}`;
  if (!item.points.every(Number.isFinite)) return 'non-finite point';
  const n = item.points.length;
  if (item.type === 'POLYGON' ? n < expectedPointCount('POLYGON') || n % 2 !== 0 : n !== expectedPointCount(item.type)) {
    return `${item.type} needs ${item.type === 'POLYGON' ? '>= 6 (even)' : expectedPointCount(item.type)} numbers, got ${n}`;
  }
  if (ROI_TYPES.has(item.type)) {
    const area = item.results.find((r) => r[2] === 'area');
    if (!area || !(Number(area[0]) > MIN_AREA)) return 'area must be > 1e-6';
  }
  if (TEXT_TYPES.has(item.type)) {
    const text = item.results.find((r) => r[2] === 'text');
    if (typeof text?.[0] !== 'string' || text[0].length > MAX_TEXT_LENGTH) return 'text must be a string of at most 500 characters';
  }
  return undefined;
}

// --- toStored -----------------------------------------------------------------------------------------------

function spacingOfContext(context: ImageContext): Spacing {
  return spacingFrom(context.rowPixelSpacing, context.columnPixelSpacing);
}

function lengthUnit(spacing: Spacing): ResultUnit {
  return spacing.calibrated ? 'mm' : 'pixels';
}

function isPoint(value: unknown): value is Point {
  const p = value as Point | undefined;
  return p !== undefined && p !== null && Number.isFinite(p.x) && Number.isFinite(p.y);
}

/** Polygon vertices: the beta's freehand tool keeps them as an array in `handles`. */
function polygonVertices(data: MeasurementData): Point[] | undefined {
  const handles: unknown = data.handles;
  if (!Array.isArray(handles)) return undefined;
  const vertices = handles.filter(isPoint);
  return vertices.length === handles.length && vertices.length >= MIN_POLYGON_VERTICES ? vertices : undefined;
}

function orderedHandles(data: MeasurementData, order: readonly string[]): Point[] | undefined {
  const points = order.map((key) => data.handles[key] as unknown);
  return points.every(isPoint) ? (points as Point[]) : undefined;
}

interface RoiStats {
  mean?: number;
  stdDev?: number;
  variance?: number;
  count?: number;
}

function roiResults(area: number, data: MeasurementData, spacing: Spacing, modality: string): StoredResult[] {
  const statsUnit: ResultUnit = modality === 'CT' ? 'HU' : '';
  const results: StoredResult[] = [[area, spacing.calibrated ? 'mm^2' : 'pixels^2', 'area']];
  const stats = data.meanStdDev as RoiStats | undefined;
  if (typeof stats?.mean === 'number') results.push([stats.mean, statsUnit, 'roiMean']);
  if (typeof stats?.stdDev === 'number') results.push([stats.stdDev, statsUnit, 'roiStdDev']);
  if (typeof stats?.variance === 'number') results.push([stats.variance, statsUnit, 'roiVariance']);
  if (typeof stats?.count === 'number') results.push([stats.count, 'pixels', 'numberOfPixels']);
  const suv = (data.meanStdDevSUV as RoiStats | undefined)?.mean;
  if (modality === 'PT' && typeof suv === 'number' && Number.isFinite(suv)) results.push([suv, 'g/ml', 'SUV']);
  return results;
}

interface ProbeCache {
  value?: number;
  suv?: number;
  rgb?: string;
}

function pointResults(data: MeasurementData, modality: string): StoredResult[] {
  const probe = data.probe as ProbeCache | undefined;
  if (modality === 'PT' && typeof probe?.suv === 'number') return [[probe.suv, 'g/ml', 'SUV']];
  const unit: ResultUnit = modality === 'CT' ? 'HU' : '';
  if (typeof probe?.value === 'number') return [[probe.value, unit, 'pixelIntensity']];
  if (typeof probe?.rgb === 'string') return [[probe.rgb, '', 'pixelIntensity']];
  return [['', unit, 'pixelIntensity']];
}

/** Results of a type measured from its handles alone (everything except POINT and the ROIs' statistics). */
function geometricResults(type: AnnotationType, h: HandleMap, spacing: Spacing): StoredResult[] | undefined {
  const unit = lengthUnit(spacing);
  switch (type) {
    case 'LINE':
      return [[measureLength(h, spacing), unit, 'length']];
    case 'ANGLE': {
      const angle = measureAngle(h, spacing);
      return angle === undefined ? undefined : [[round(angle, DECIMALS.angle), 'deg', 'angle']];
    }
    case 'COBB-ANGLE':
      return [[round(measureCobb(h, spacing), DECIMALS.angle), 'deg', 'cobbAngle']];
    case 'FEMORAL-ANTEVERSION':
      return [[round(measureTwoLineAngle(h, spacing), DECIMALS.angle), 'deg', 'angle']];
    case 'BIDIRECTIONAL': {
      const { length, width } = measureBidirectional(h, spacing);
      return [[round(length, DECIMALS.bidirectional), unit, 'length'], [round(width, DECIMALS.bidirectional), unit, 'width']];
    }
    case 'PARALLEL-DISTANCE': {
      const value = measureParallelDistance(h, spacing);
      return value === undefined ? undefined : [[round(value, DECIMALS.length), unit, 'distance']];
    }
    case 'CARDIOTHORACIC-INDEX': {
      const { A, B, C, index } = measureCardiothoracic(h, spacing);
      if (index === undefined) return undefined;
      const r = (v: number) => round(v, DECIMALS.cardiothoracicLength);
      return [[round(index, DECIMALS.index), '', 'index'], [r(A), unit, 'A'], [r(B), unit, 'B'], [r(C), unit, 'C']];
    }
    case 'INSALL-SALVATI': {
      const { A, B, ratioAB, ratioBA } = measureInsallSalvati(h, spacing);
      if (ratioAB === undefined || ratioBA === undefined) return undefined;
      return [
        [round(ratioAB, DECIMALS.ratio), '', 'ratio AB'],
        [round(ratioBA, DECIMALS.ratio), '', 'ratio BA'],
        [round(A, DECIMALS.length), unit, 'A'],
        [round(B, DECIMALS.length), unit, 'B'],
      ];
    }
    case 'COXOMETRY': {
      const v = measureCoxometry(h, spacing);
      if (!v) return undefined;
      const r = (x: number) => round(x, DECIMALS.length);
      return [
        [round(v.D, DECIMALS.angle), 'deg', 'D'], [round(v.I, DECIMALS.angle), 'deg', 'I'],
        [r(v.D1), unit, 'D1'], [r(v.D2), unit, 'D2'], [r(v.H1), unit, 'H1'], [r(v.H2), unit, 'H2'],
      ];
    }
    default:
      return undefined;
  }
}

/**
 * One measurement → wire format. Returns null when it cannot be saved: unknown tool, still being drawn,
 * missing handles, a degenerate geometry (zero-length axis, zero area) or an empty text.
 */
export function toStored(toolName: string, data: MeasurementData, context: ImageContext): StoredAnnotation | null {
  const type = TYPE_BY_TOOL[toolName];
  if (!type || data.complete === false) return null;
  const spacing = spacingOfContext(context);

  if (type === 'POLYGON') {
    const vertices = polygonVertices(data);
    if (!vertices) return null;
    const area = polygonArea(vertices, spacing);
    if (!(area > MIN_AREA)) return null;
    return { type, points: vertices.flatMap((p) => [p.x, p.y]), results: roiResults(area, data, spacing, context.modality) };
  }

  const handles = orderedHandles(data, HANDLE_ORDER[type]);
  if (!handles) return null;
  const points = handles.flatMap((p) => [p.x, p.y]);
  const h = data.handles as HandleMap;

  if (TEXT_TYPES.has(type)) {
    const text = typeof data.text === 'string' ? data.text.slice(0, MAX_TEXT_LENGTH) : '';
    return text ? { type, points, results: [[text, '', 'text']] } : null;
  }
  if (type === 'POINT') return { type, points, results: pointResults(data, context.modality) };
  if (type === 'ELLIPSE' || type === 'RECTANGLE') {
    const area = (type === 'ELLIPSE' ? ellipseArea : rectangleArea)(h.start, h.end, spacing);
    if (!(area > MIN_AREA)) return null;
    return { type, points, results: roiResults(area, data, spacing, context.modality) };
  }
  const results = geometricResults(type, h, spacing);
  return results ? { type, points, results } : null;
}

// --- fromStored ---------------------------------------------------------------------------------------------

function handle(x: number, y: number): { x: number; y: number; highlight: boolean; active: boolean } {
  return { x, y, highlight: true, active: false };
}

function textBox(): MeasurementData['handles'][string] {
  return { x: 0, y: 0, active: false, hasMoved: false, movesIndependently: false, drawnIndependently: true, allowedOutsideImage: true, hasBoundingBox: true };
}

function pairs(points: readonly number[]): Point[] {
  const out: Point[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) out.push({ x: points[i], y: points[i + 1] });
  return out;
}

function storedText(item: StoredAnnotation): string {
  const text = item.results.find((r) => r[2] === 'text')?.[0];
  return typeof text === 'string' ? text : '';
}

/** The beta freehand tool's state: vertices linked in a closed ring, plus its text box. */
function polygonData(vertices: Point[]): MeasurementData {
  const ring = vertices.map((p, i) => {
    const next = vertices[(i + 1) % vertices.length];
    return { ...handle(p.x, p.y), lines: [{ x: next.x, y: next.y }] };
  });
  const handles = Object.assign(ring, { textBox: textBox(), invalidHandlePlacement: false });
  const xs = vertices.map((p) => p.x);
  const ys = vertices.map((p) => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return {
    visible: true,
    active: false,
    invalidated: true,
    highlight: false,
    canComplete: false,
    complete: true,
    polyBoundingBox: { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top },
    handles: handles as unknown as MeasurementData['handles'],
  };
}

/**
 * Wire format → a measurement for cornerstone-tools. Only the handles (and text) are taken from the item;
 * values are recomputed by the tool on render. Returns null for an unknown type or a wrong point count.
 * `context` is accepted for symmetry with toStored (geometry needs no spacing to be placed).
 */
export function fromStored(item: StoredAnnotation, context?: ImageContext): ToolMeasurement | null {
  void context;
  if (validateStored({ ...item, results: fillForValidation(item) }) !== undefined) return null;
  const toolName = TOOL_BY_TYPE[item.type];
  const points = pairs(item.points);
  if (item.type === 'POLYGON') return { toolName, data: polygonData(points) };

  const order = HANDLE_ORDER[item.type];
  const handles: MeasurementData['handles'] = {};
  order.forEach((key, i) => {
    handles[key] = handle(points[i].x, points[i].y);
  });
  const data: MeasurementData = { visible: true, active: false, invalidated: true, complete: true, handles };
  switch (item.type) {
    case 'POINT':
      break;
    case 'TEXT':
      handles.end.hasBoundingBox = true;
      data.text = storedText(item);
      break;
    case 'SPINE-LABELING':
      data.text = storedText(item);
      break;
    case 'ARROW':
      handles.textBox = textBox();
      data.text = storedText(item);
      break;
    default:
      handles.textBox = textBox();
      if (order === TWO_LINES.order) data.segmentsDrawn = 2;
      data.locked = false;
  }
  return { toolName, data };
}

/** Geometry-only validation on load: stored values are ignored, except that text types need their text. */
function fillForValidation(item: StoredAnnotation): StoredResult[] {
  if (ROI_TYPES.has(item.type)) return [[1, '', 'area']];
  if (TEXT_TYPES.has(item.type)) return [[storedText(item).slice(0, MAX_TEXT_LENGTH), '', 'text']];
  return item.results;
}
